import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@/auth/AuthContext';
import styles from './ui.module.css';
import logo from '@/assets/hoseaus-logo.png';

/** App-Rahmen mit Header (Marke + Burger-Menü) und Content-Outlet. */
export function Layout() {
  const { user, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuAreaRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!menuOpen) return;
    function handlePointerDown(event: MouseEvent) {
      if (menuAreaRef.current && !menuAreaRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    }
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setMenuOpen(false);
    }
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [menuOpen]);

  return (
    <>
      <header className={styles.header}>
        <Link to="/matchdays" className={styles.brandWrap}>
          <img src={logo} alt="" className={styles.brandLogo} />
          <span className={styles.brandText}>
            <span className={styles.brand}>HoseAus</span>
            <span className={styles.brandEyebrow}>Beer & Love - Tippspiel</span>
          </span>
        </Link>
        {user && (
          <div className={styles.headerRight} ref={menuAreaRef}>
            <span className={styles.userName}>{user.display_name}</span>
            <button
              type="button"
              className={styles.burgerBtn}
              aria-label="Menü"
              aria-haspopup="true"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((open) => !open)}
            >
              <span className={styles.burgerLine} />
              <span className={styles.burgerLine} />
              <span className={styles.burgerLine} />
            </button>
            {menuOpen && (
              <nav className={styles.menuPanel} aria-label="Hauptnavigation">
                <Link to="/matchdays" className={styles.menuLink}>
                  Tippabgabe
                </Link>
                <Link to="/tippuebersicht" className={styles.menuLink}>
                  Tippübersicht
                </Link>
                <Link to="/standings" className={styles.menuLink}>
                  Gesamtübersicht
                </Link>
                <Link to="/profil" className={styles.menuLink}>
                  Mein Profil
                </Link>
                <Link to="/feedback" className={styles.menuLink}>
                  Feedback
                </Link>
                {user.role === 'admin' && (
                  <Link to="/admin" className={`${styles.menuLink} ${styles.menuLinkAdmin}`}>
                    Admin
                  </Link>
                )}
                <button type="button" className={styles.menuLogout} onClick={() => void logout()}>
                  Abmelden
                </button>
              </nav>
            )}
          </div>
        )}
      </header>
      <div
        className={styles.watermark}
        style={{ '--watermark-logo': `url(${logo})` } as CSSProperties}
        aria-hidden="true"
      />
      <main className={styles.page}>
        <Outlet />
      </main>
    </>
  );
}
